import { FEATURES } from "@src/constants";
import { IPlaylistFilter } from "@src/redux/slices/playlistSlice";
import {
  ApiResponse,
  PaginationParams,
  SortingParams,
} from "@src/types/httpApi";
import {
  CreatePlaylistBody,
  IPlaylistRecord,
  PlaylistActionType,
  PlaylistStatus,
} from "@src/types/playlist";
import httpApi from "@utils/httpApi";
import { AxiosResponse } from "axios";

// Fetch playlists with query parameters
export const fetchPlaylists = (
  filtersApplied: IPlaylistFilter &
    PaginationParams & {
      sorting: SortingParams;
      curatorId: string;
      isSubcurator?: boolean;
      subcuratorsIds?: string[];
    }
): Promise<AxiosResponse<ApiResponse<IPlaylistRecord[]>>> => {
  const queryParams = new URLSearchParams();

  // Sorting parameters
  if (filtersApplied.sorting?.field) {
    queryParams.append(
      "sort",
      `${filtersApplied.sorting.field}:${filtersApplied.sorting.direction}`
    );
  }

  // Filtering conditions
  const filters: Record<string, string | undefined> = {
    "filters[playlistStatus]": filtersApplied.playlistStatus || undefined,
    "filters[overwriteShowDescription][$containsi]":
      filtersApplied.description || undefined,
  };

  // Handle `tracksDuration` range filter
  if (
    filtersApplied.durationInMinutes &&
    filtersApplied.durationInMinutes > 0
  ) {
    filters["filters[tracksDuration][$gte]"] = String(
      filtersApplied.durationInMinutes * 60
    );
    filters["filters[tracksDuration][$lt]"] = String(
      (filtersApplied.durationInMinutes + 1) * 60
    );
  }

  // Role-based filters
  if (
    (filtersApplied.role === "admin" || filtersApplied.role === "manager") &&
    !filtersApplied?.isSubcurator
  ) {
    // Admin/Manager: Gets everything unless `showId` is specified
    if (filtersApplied.showId) {
      filters["filters[show][documentId][$eq]"] = filtersApplied.showId;
    }
    if (filtersApplied.isForMood === true) {
      filters["filters[moodFactory][id][$notNull]"] = "true";
    } else {
      // Exclude playlists with moodFactory when not in Moods view (false or undefined)
      filters["filters[moodFactory][id][$null]"] = "true";
    }
    if (filtersApplied.guestCuratorId) {
      filters["filters[curator][documentId][$eq]"] =
        filtersApplied.guestCuratorId;
    }
    if (filtersApplied.curatorId) {
      filters["filters[curator][documentId][$eq]"] = filtersApplied.curatorId;
    }
  } else if (filtersApplied.isSubcurator) {
    // Subcurators: Can only see their own playlists
    filters["filters[curator][documentId][$eq]"] = filtersApplied.curatorId;
    // For non-moods view, also hide mood playlists
    if (filtersApplied.isForMood === true) {
      filters["filters[moodFactory][id][$notNull]"] = "true";
    } else {
      filters["filters[moodFactory][id][$null]"] = "true";
    }
  } else {
    // Curators: Can see their own playlists + playlists created by their subcurators
    const allowedCuratorIds = [
      filtersApplied.curatorId,
      ...(filtersApplied.subcuratorsIds || []),
    ].filter(Boolean);
    if (allowedCuratorIds.length > 0) {
      allowedCuratorIds.forEach((curatorId) => {
        queryParams.append(`filters[curator][documentId][$eq]`, curatorId);
      });
    }
    // For non-moods view, also hide mood playlists
    if (filtersApplied.isForMood === true) {
      filters["filters[moodFactory][id][$notNull]"] = "true";
    } else {
      filters["filters[moodFactory][id][$null]"] = "true";
    }
  }

  // Append non-empty filters
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined) queryParams.append(key, value);
  });

  // Date range filter
  if (
    filtersApplied?.lastModified?.startDate &&
    filtersApplied?.lastModified?.endDate
  ) {
    queryParams.append(
      "filters[updatedAt][$gte]",
      filtersApplied.lastModified.startDate.toISOString()
    );
    queryParams.append(
      "filters[updatedAt][$lte]",
      filtersApplied.lastModified.endDate.toISOString()
    );
  }

  // Date aired filter (using releaseDate)
  if (
    filtersApplied?.dateAired?.startDate ||
    filtersApplied?.dateAired?.endDate
  ) {
    const startDate =
      filtersApplied.dateAired.startDate || filtersApplied.dateAired.endDate;
    const endDate =
      filtersApplied.dateAired.endDate || filtersApplied.dateAired.startDate;

    if (startDate && endDate) {
      // If both dates are the same day, search for the entire day
      const startOfDay = new Date(startDate);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);

      // Check if it's the same day
      const isSameDay =
        startDate.getDate() === endDate.getDate() &&
        startDate.getMonth() === endDate.getMonth() &&
        startDate.getFullYear() === endDate.getFullYear();

      if (isSameDay) {
        // Single day selection: search from start of day to end of day
        queryParams.append(
          "filters[releaseDate][$gte]",
          startOfDay.toISOString()
        );
        queryParams.append(
          "filters[releaseDate][$lte]",
          endOfDay.toISOString()
        );
      } else {
        // Date range: use the dates as provided
        queryParams.append(
          "filters[releaseDate][$gte]",
          startDate.toISOString()
        );
        queryParams.append("filters[releaseDate][$lte]", endDate.toISOString());
      }
    }
  }

  // Search title filter
  const titleFilter =
    filtersApplied.searchText?.trim().toLowerCase() ||
    filtersApplied.playlistName?.trim().toLowerCase();
  if (titleFilter) {
    queryParams.append(
      "filters[$or][2][documentId][$containsi]",
      filtersApplied.searchText?.trim() ?? ""
    );
    queryParams.append("filters[$or][0][title][$containsi]", titleFilter);
    queryParams.append("filters[$or][1][id][$containsi]", titleFilter);
    queryParams.append(
      "filters[$or][2][overwriteShowDescription][$containsi]",
      titleFilter
    );
    queryParams.append("filters[$or][3][show][title][$containsi]", titleFilter);
    queryParams.append("filters[$or][4][notes][$containsi]", titleFilter);
    queryParams.append(
      "filters[$or][5][playlistStatus][$containsi]",
      titleFilter
    );
    queryParams.append(
      "filters[$or][6][curator][name][$containsi]",
      titleFilter
    );
    queryParams.append("filters[$or][7][documentId][$containsi]", titleFilter);
  }

  // Pagination parameters
  const pagination = {
    "pagination[page]": filtersApplied.page,
    "pagination[pageSize]": filtersApplied.pageSize,
  };

  Object.entries(pagination).forEach(([key, value]) => {
    if (value !== undefined) queryParams.append(key, String(value));
  });
  if (filtersApplied.playlistProcessedAudioNotNull) {
    queryParams.append("filters[playlistProcessedAudio][$null]", "false");
  }
  if (filtersApplied.playlistProcessedAudioDurationGte) {
    queryParams.append(
      "filters[playlistProcessedAudioDuration][$gte]",
      String(filtersApplied.playlistProcessedAudioDurationGte)
    );
  }
  // Fields to populate
  const populateFields = [
    "populate[show][fields][0]=title",
    "populate[show][fields][1]=documentId",
    "populate[show][populate][coverDesktop][fields][0]=*",
    "populate[tracks][fields][0]=documentId",
    "populate[tracks][fields][1]=duration",
    "populate[curator][fields][0]=name",
    "populate[curator][fields][1]=documentId",
    "populate[show][populate][curators][fields][0]=documentId",
    "populate[moodFactory][fields][0]=*",
    "populate[playlistProcessedAudio][fields][0]=*",
    "fields=id",
    "fields=documentId",
    "fields=title",
    "fields=updatedAt",
    "fields=playlistStatus",
    "fields=notes",
    "fields=overwriteShowDescription",
    "fields=tracksDuration",
    "fields=releaseDate",
    "populate[overwriteShowRadioImage][fields][0]=*",
    "populate[show][populate][radioImage][fields][0]=*",
  ];

  populateFields.forEach((field) => {
    const [key, value] = field.split("=");
    queryParams.append(key, value);
  });

  return httpApi.get(`/api/playlists?${queryParams.toString()}`);
};

export const fetchPlaylistsByShowId = (
  showId: string,
  searchText: string
): Promise<AxiosResponse<ApiResponse<IPlaylistRecord[]>>> => {
  const queryParams = new URLSearchParams();
  queryParams.append("filters[show][documentId][$eq]", showId);

  if (searchText) {
    queryParams.append("filters[title][$containsi]", searchText);
  }
  return httpApi.get(`/api/playlists?${queryParams.toString()}`);
};

// Create a new playlist
export const createPlaylistApi = async (
  createPlaylistBody: CreatePlaylistBody
): Promise<AxiosResponse<any>> => {
  const payload: any = {
    data: {
      title: createPlaylistBody.playlistName,
      notes: createPlaylistBody.notes,
      overwriteShowDescription:
        createPlaylistBody.overwriteShowDescription || null,
      show: !!createPlaylistBody.assignedShow
        ? {
            set: [{ documentId: createPlaylistBody.assignedShow }],
          }
        : undefined,
      moodFactory: !!createPlaylistBody.moodFactory
        ? {
            set: [{ documentId: createPlaylistBody.moodFactory }],
          }
        : undefined,
      curator: !!createPlaylistBody.curator
        ? {
            set: [{ documentId: createPlaylistBody.curator }],
          }
        : undefined,
      jingle: !!createPlaylistBody.jingle
        ? {
            set: [{ documentId: createPlaylistBody.jingle }],
          }
        : undefined,
      playlistStatus: "draft",
    },
  };
  return httpApi.post("/api/playlists", payload);
};

// Update a playlist
export const updatePlaylistApi = async (
  createPlaylistBody: Partial<CreatePlaylistBody>,
  playlistId: string
): Promise<AxiosResponse<any>> => {
  const dataToSend: {
    title?: string;
    notes?: string;
    overwriteShowDescription: string | null;
    show?: { set: { documentId: string }[] };
    curator?: { set: { documentId: string }[] };
    jingle?: { set: { documentId: string }[] };
  } = {
    title: createPlaylistBody.playlistName,
    notes: createPlaylistBody.notes,
    overwriteShowDescription:
      createPlaylistBody.overwriteShowDescription || null,
  };

  if (createPlaylistBody.assignedShow) {
    dataToSend["show"] = {
      set: [{ documentId: createPlaylistBody.assignedShow }],
    };
  }
  if (createPlaylistBody.curator) {
    dataToSend["curator"] = {
      set: [{ documentId: createPlaylistBody.curator }],
    };
  }
  if (createPlaylistBody.jingle) {
    dataToSend["jingle"] = {
      set: [{ documentId: createPlaylistBody.jingle }],
    };
  }
  return httpApi.put(`/api/playlists/${playlistId}`, {
    data: dataToSend,
  });
};
export const updatePlaylistTagsApi = (
  playlistId: string,
  selectedCategories: string[]
) => {
  return httpApi.put(`/api/playlists/${playlistId}`, {
    data: {
      playlistTags: {
        set: selectedCategories.map((categoryId) => ({
          documentId: categoryId,
        })),
      },
    },
  });
};

/**
 * Replace the playlist's subtags. Subtags hang off the tags the curator picked,
 * so this always runs right after `updatePlaylistTagsApi` [ROVR-3982].
 */
export const updatePlaylistSubtagsApi = (
  playlistId: string,
  selectedSubtags: string[]
) => {
  return httpApi.put(`/api/playlists/${playlistId}`, {
    data: {
      subPlaylistTags: {
        set: selectedSubtags.map((subtagId) => ({
          documentId: subtagId,
        })),
      },
    },
  });
};

export const updatePlaylistRecommendationTagsApi = (
  playlistId: string,
  recommendationTagIds: string[]
) => {
  return httpApi.put(`/api/playlists/${playlistId}`, {
    data: {
      playlistRecommendationTags: {
        set: recommendationTagIds.map((tagId) => ({
          documentId: tagId,
        })),
      },
    },
  });
};

// reset processed playlist – uses the dedicated /reset endpoint, which cancels
// any in-progress batch process job AND clears the error / processed-mix state
// so the automatic process can pick the playlist again (ROVR-3798). Replaces the
// previous manual PUT, which cleared the fields but left a running job going.
export const resetProcessedPlaylistApi = async (
  playlistId: string
): Promise<AxiosResponse<any>> => {
  return httpApi.post(`/api/playlists/${playlistId}/reset`);
};

// Delete a playlist
export const deletePlaylistApi = async (
  playlistId: string
): Promise<AxiosResponse<any>> => {
  return httpApi.delete(`/api/playlists/${playlistId}`);
};

// Playlist Details Page APIs
export const fetchPlaylistTracksApi = (
  playlistId: string,
  filters: { searchText: string }
): Promise<AxiosResponse<any>> => {
  const queryParams = new URLSearchParams();
  if (filters.searchText) {
    queryParams.append(
      "filters[$or][0][title][$containsi]",
      filters.searchText
    );
    queryParams.append("filters[$or][1][isrc][$containsi]", filters.searchText);
    queryParams.append(
      "filters[$or][2][id][$containsi]",
      filters.searchText.trim().replace(/\s+/g, "")
    );
    queryParams.append("filters[$or][3][bpm][$containsi]", filters.searchText);
    queryParams.append(
      "filters[$or][4][endKey][$containsi]",
      filters.searchText
    );
    queryParams.append(
      "filters[$or][5][endBpm][$containsi]",
      filters.searchText
    );
    queryParams.append(
      "filters[$or][6][artist][$containsi]",
      filters.searchText
    );
    queryParams.append(
      "filters[$or][7][label][$containsi]",
      filters.searchText
    );
    queryParams.append(
      "filters[$or][8][bitrate][$containsi]",
      filters.searchText
    );
    queryParams.append(
      "filters[$or][9][album][$containsi]",
      filters.searchText
    );
  }
  queryParams.append("pagination[page]", "1");
  queryParams.append("pagination[pageSize]", "300");
  queryParams.append("populate[tags][fields]", "title,description");
  queryParams.append("populate[genre][fields]", "title,description");
  queryParams.append("populate[genre][populate][subGenres][fields]", "*");
  queryParams.append("populate[subGenre][fields]", "*");
  queryParams.append("populate[emotion][fields]", "title,description");
  queryParams.append("populate[audio][fields]", "*");

  const fields = [
    "title",
    "artist",
    "album",
    "duration",
    "bitrate",
    "bpm",
    "key",
    "label",
    "notes",
    "year",
    "id",
    "documentId",
    "isrc",
    "endBpm",
    "endKey",
    "fadeInTime",
    "fadeOutTime",
    "createdAt",
    "updatedAt",
    "publishedAt",
    "albumCoverUrl",
    "metadata",
    "shazamInfo",
    "youtubeInformation",
    ...(FEATURES.TRACK_COVER_OVERRIDE_ENABLED ? ["coverUrl"] : []),
  ];
  fields.forEach((field, index) => {
    queryParams.append(`fields[${index}]`, field);
  });

  return httpApi.get(
    `api/playlists/${playlistId}/tracks?${queryParams.toString()}`
  );
};
export const fetchPlaylistDetailsApi = (
  playlistId: string
): Promise<AxiosResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.append("populate[playlistProcessedAudio][fields][0]", "*");
  queryParams.append("populate[waveform][fields][0]", "*");
  queryParams.append("populate[waveformBinary][fields][0]", "*");
  queryParams.append("populate[show][fields][0]", "*");
  // Populate curators for the show relation
  queryParams.append(
    "populate[show][populate][curators][fields][0]",
    "documentId"
  );
  queryParams.append("populate[curator][fields][0]", "name");
  queryParams.append("populate[playlistTags][fields][0]", "*");
  // Strapi answers a populate key with no matching relation with a 400, so this
  // stays behind the flag like the tag list's own populate [ROVR-3982].
  if (FEATURES.PLAYLIST_SUBTAGS_ENABLED) {
    queryParams.append("populate[subPlaylistTags][fields][0]", "label");
    queryParams.append("populate[subPlaylistTags][fields][1]", "displayOrder");
  }
  queryParams.append("populate[playlistRecommendationTags][fields][0]", "*");
  queryParams.append("populate[jingle][fields][0]", "title");
  queryParams.append("populate[jingle][fields][1]", "documentId");
  queryParams.append("populate[overwriteShowRadioImage][fields][0]", "*");
  queryParams.append("populate[show][populate][radioImage][fields][0]", "*");

  const fields = [
    "title",
    "playlistStatus",
    "playlistAction",
    "overwriteShowDescription",
    "processingErrorMessage",
    "playlistProcessedAudioDuration",
    "tracksDuration",
    "playlistProcessing",
    "stitchedTracksMetadata",
  ];
  fields.forEach((field, index) => {
    queryParams.append(`fields[${index}]`, field);
  });
  return httpApi.get(`api/playlists/${playlistId}?${queryParams.toString()}`);
};

export const fetchPlaylistPeaksApi = (
  playlistId: string
): Promise<AxiosResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.append("fields[0]", "playlistPeaks");
  return httpApi.get(`api/playlists/${playlistId}?${queryParams.toString()}`);
};
export const updatePlaylistActionApi = (
  playlistId: string,
  action: PlaylistActionType
): Promise<AxiosResponse> => {
  return httpApi.put(`api/playlists/${playlistId}`, {
    data: {
      playlistStatus: "ready",
      playlistAction: action,
    },
  });
};

export const updatePlaylistJingleApi = (
  playlistId: string,
  jingleDocumentId: string | null
): Promise<AxiosResponse> => {
  return httpApi.put(`api/playlists/${playlistId}`, {
    data: {
      jingle: jingleDocumentId
        ? { set: [{ documentId: jingleDocumentId }] }
        : { set: [] },
    },
  });
};
export const updatePlaylistStatusApi = (
  playlistId: string,
  status: PlaylistStatus
): Promise<AxiosResponse> => {
  return httpApi.put(`api/playlists/${playlistId}`, {
    data: {
      playlistStatus: status,
    },
  });
};

export const submitPlaylistApi = async (
  playlistId: string,
  playlistTagsIds: string[],
  playlistSubtagsIds?: string[]
): Promise<AxiosResponse> => {
  await updatePlaylistTagsApi(playlistId, playlistTagsIds);
  // Left out while the backend has subtags off, where Strapi has no relation to
  // write them to [ROVR-4246].
  if (playlistSubtagsIds) {
    await updatePlaylistSubtagsApi(playlistId, playlistSubtagsIds);
  }
  return updatePlaylistStatusApi(playlistId, "submitted");
};

export const updatePlaylistTrackOrderApi = (
  playlistId: string,
  tracksIds: string[]
): Promise<AxiosResponse> => {
  const tracksIdsWithDocumentId = tracksIds.map((id) => ({
    documentId: id,
  }));
  return httpApi.put(`api/playlists/${playlistId}`, {
    data: {
      tracks: {
        connect: tracksIdsWithDocumentId,
      },
    },
  });
};

export const removeTrackFromPlaylistApi = async (
  playlistId: string,
  trackId: string
): Promise<AxiosResponse> => {
  await httpApi.put(`api/tracks/${trackId}`, {
    data: {
      playlist: {
        disconnect: [{ documentId: playlistId }],
      },
    },
  });

  return httpApi.put(`api/playlists/${playlistId}`, {
    data: {
      tracks: {
        disconnect: [{ documentId: trackId }],
      },
    },
  });
};

export type AudioDownloadUrl = { url: string; expiresIn: number };

// Short-lived presigned S3 link for the processed mix. The backend checks who may download.
export const fetchPlaylistDownloadUrlApi = async (
  playlistId: string
): Promise<AxiosResponse<AudioDownloadUrl>> => {
  return httpApi.post(`/api/playlists/${playlistId}/download-url`);
};
